import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Building2, Phone, MapPin } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Branches() {
  const { getAuthHeaders, user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', address: '', phone: '', default_opening_fund: '' });
  const [editId, setEditId] = useState(null);

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: getAuthHeaders() });
      setBranches(res.data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, default_opening_fund: parseFloat(form.default_opening_fund) || 0 };
      if (editId) {
        await axios.put(`${API}/api/branches/${editId}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/api/branches`, payload, { headers: getAuthHeaders() });
      }
      setShowForm(false);
      setForm({ name: '', address: '', phone: '', default_opening_fund: '' });
      setEditId(null);
      fetchBranches();
    } catch (err) { console.error(err); }
  };

  const editBranch = (branch) => {
    setForm({
      name: branch.name, address: branch.address, phone: branch.phone,
      default_opening_fund: String(branch.default_opening_fund ?? ''),
    });
    setEditId(branch.id);
    setShowForm(true);
  };

  const deleteBranch = async (id) => {
    if (!window.confirm('Eliminar esta sucursal?')) return;
    try {
      await axios.delete(`${API}/api/branches/${id}`, { headers: getAuthHeaders() });
      fetchBranches();
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="branches-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Sucursales</h1>
        {user?.role === 'admin' && (
          <Button onClick={() => { setEditId(null); setForm({ name: '', address: '', phone: '', default_opening_fund: '' }); setShowForm(true); }} className="bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="new-branch-button">
            <Plus className="w-4 h-4 mr-2" /> Nueva Sucursal
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {branches.map((branch) => (
          <div key={branch.id} className="bg-white rounded-lg border border-slate-200 p-5 hover:shadow-sm transition-shadow" data-testid={`branch-${branch.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-lg bg-[#1D3C6E]/10 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#1D3C6E]" />
              </div>
              {user?.role === 'admin' && (
                <div className="flex gap-1">
                  <button onClick={() => editBranch(branch)} className="text-xs text-slate-500 hover:text-[#1D3C6E]">Editar</button>
                  <button onClick={() => deleteBranch(branch.id)} className="text-xs text-red-400 hover:text-red-600 ml-2">Eliminar</button>
                </div>
              )}
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">{branch.name}</h3>
            <div className="space-y-1 text-sm text-slate-600">
              {branch.address && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" />{branch.address}</div>}
              {branch.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5" />{branch.phone}</div>}
              <div className="flex items-center gap-2 pt-1 text-xs text-slate-500">
                <span>Fondo apertura:</span>
                <span className="font-semibold text-slate-700">${(branch.default_opening_fund || 0).toFixed(2)}</span>
              </div>
            </div>
          </div>
        ))}
        {branches.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400">
            <Building2 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No hay sucursales registradas</p>
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">{editId ? 'Editar' : 'Nueva'} Sucursal</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input data-testid="branch-name-input" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Direccion</label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><label className="text-xs font-medium text-slate-600">Telefono</label><Input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
            <div>
              <label className="text-xs font-medium text-slate-600">Fondo de apertura predeterminado</label>
              <Input type="number" step="0.01" value={form.default_opening_fund}
                onChange={(e) => setForm({...form, default_opening_fund: e.target.value})}
                placeholder="0.00" data-testid="branch-default-fund-input" />
              <p className="text-[11px] text-slate-400 mt-1">Monto que se pre-llena al abrir la caja en esta sucursal.</p>
            </div>
            <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="submit-branch-button">Guardar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
